/**
 * A CAD-style preview of a body or branch part: the core, a plate at every
 * port, and dimension lines whose numbers can be clicked and retyped.
 *
 * Dimensions are the preset's own numbers (half length, half width,
 * radius, hip drop...) for a preset, and each port's x / y / z and
 * roll / pitch / yaw for a custom part; editing one writes straight
 * back into the spec.  Port labels sit on leader lines pushed outward
 * from the part so they do not cover each other or the plates.
 */

import { useMemo, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { Html, Line, OrbitControls } from "@react-three/drei";
import { Euler, Vector3 } from "three";
import type { PrintBodyPort } from "../api/types";

export interface Dimension {
  key: string;
  /** what the number means, shown before it */
  label: string;
  value: number;
  /** start and end of the measured span, mm */
  from: [number, number, number];
  to: [number, number, number];
  /** where the dimension line is drawn, offset from the span (mm); default: 45 mm along +z */
  offset?: [number, number, number];
  onChange: (value: number) => void;
}

interface BodyPreviewProps {
  ports: PrintBodyPort[];
  dimensions: Dimension[];
  coreShape: "box" | "hexagon" | "circle" | "hub";
  /** part colour as #rrggbb; the printed-part pink when omitted */
  color?: string;
  /** custom parts: clicking a port's numbers edits its position (field "xyz") or orientation ("rpy") */
  onPortChange?: (index: number, field: "xyz" | "rpy", component: number, value: number) => void;
}

const MM = 0.001;
const PLATE = 45;
const PLATE_T = 6.5;
const PART_GREY = "#c0c0c0";  // the stock connectors' grey
const HOVER_LIGHTEN = "#d8d8d8";
const ACCENT = "#d15c6b";  // editable numbers, so they read as controls
const INK = "#1f2328";
const LEADER = "#8a8378";
const ARROW = 10; // mm

/** URDF rpy (static x-y-z) as a three.js Euler: R = Rz · Ry · Rx. */
function eulerOf(rpy: [number, number, number]): Euler {
  const r = (d: number) => (d * Math.PI) / 180;
  return new Euler(r(rpy[0]), r(rpy[1]), r(rpy[2]), "ZYX");
}

const chipStyle = {
  fontSize: 11,
  fontFamily: "monospace",
  background: "rgba(255,255,255,0.95)",
  border: "1px solid #b9b3a7",
  borderRadius: 3,
  padding: "1px 5px",
  whiteSpace: "nowrap" as const,
  color: INK,
};

function NumberChip({
  label,
  value,
  title,
  ariaLabel,
  onCommit,
}: {
  label: string;
  value: number;
  title: string;
  ariaLabel: string;
  onCommit: (value: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(String(value));
  const commit = () => {
    const parsed = parseFloat(text);
    if (!Number.isNaN(parsed)) onCommit(parsed);
    setEditing(false);
  };
  if (editing) {
    return (
      <input
        autoFocus
        aria-label={ariaLabel}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        style={{ width: 52, fontSize: 11, padding: "0 3px", border: `1px solid ${ACCENT}`, borderRadius: 3 }}
      />
    );
  }
  return (
    <button
      type="button"
      title={title}
      onClick={() => {
        setText(String(value));
        setEditing(true);
      }}
      style={{ border: "none", background: "transparent", cursor: "pointer", fontFamily: "monospace", fontSize: 11, padding: "0 2px", color: ACCENT }}
    >
      {label}
      {Math.round(value * 10) / 10}
    </button>
  );
}

/** A dimension: extension lines from the span, the dimension line offset from it, arrowheads, a clickable number. */
function DimensionMark({ dim }: { dim: Dimension }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(String(dim.value));
  const [hover, setHover] = useState(false);
  const from = new Vector3(...dim.from);
  const to = new Vector3(...dim.to);
  const offset = new Vector3(...(dim.offset ?? [0, 0, 45]));
  const a = from.clone().add(offset);
  const b = to.clone().add(offset);
  const along = b.clone().sub(a);
  const length = along.length();
  const dir = length > 1e-6 ? along.clone().normalize() : new Vector3(1, 0, 0);
  // Arrowheads open in the plane of the line and its offset direction.
  const side = offset.length() > 1e-6 ? offset.clone().normalize() : new Vector3(0, 0, 1);
  const head = (tip: Vector3, back: Vector3) => [
    [tip.clone().add(back.clone().multiplyScalar(ARROW)).add(side.clone().multiplyScalar(ARROW * 0.45)), tip],
    [tip.clone().add(back.clone().multiplyScalar(ARROW)).sub(side.clone().multiplyScalar(ARROW * 0.45)), tip],
  ];
  const mm = (v: Vector3) => v.clone().multiplyScalar(MM);
  const mid = mm(a.clone().add(b).multiplyScalar(0.5));
  const commit = () => {
    const parsed = parseFloat(text);
    if (!Number.isNaN(parsed)) dim.onChange(parsed);
    setEditing(false);
  };
  const segments: Vector3[][] = [
    [from, a.clone().add(side.clone().multiplyScalar(6))], // extension lines, a little past the dimension line
    [to, b.clone().add(side.clone().multiplyScalar(6))],
    [a, b],
    ...(length > 2 * ARROW ? [...head(a, dir), ...head(b, dir.clone().negate())] : []),
  ];
  return (
    <group>
      {segments.map((seg, k) => (
        <Line key={k} points={seg.map(mm)} color={INK} lineWidth={k < 2 ? 0.8 : 1.4} />
      ))}
      <Html position={mid} center zIndexRange={[hover || editing ? 900 : 200, 0]} style={{ pointerEvents: "auto" }}>
        <div onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
          {editing ? (
            <input
              autoFocus
              aria-label={`${dim.label} value`}
              value={text}
              onChange={(e) => setText(e.currentTarget.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") setEditing(false);
              }}
              style={{ width: 64, fontSize: 12, padding: "1px 4px", border: `1px solid ${ACCENT}`, borderRadius: 3 }}
            />
          ) : (
            <button
              type="button"
              title="Click to change"
              onClick={() => {
                setText(String(dim.value));
                setEditing(true);
              }}
              style={{ ...chipStyle, fontSize: 12, border: `1px solid ${INK}`, cursor: "pointer" }}
            >
              {dim.label} {Math.round(dim.value * 10) / 10}
            </button>
          )}
        </div>
      </Html>
    </group>
  );
}

function PortPlate({
  port,
  index,
  labelAt,
  color,
  onPortChange,
}: {
  port: PrintBodyPort;
  index: number;
  /** where the label hangs, mm, in the part's frame */
  labelAt: Vector3;
  color: string;
  onPortChange?: BodyPreviewProps["onPortChange"];
}) {
  const [hover, setHover] = useState(false);
  const position = new Vector3(...port.xyz);
  const euler = eulerOf(port.rpy);
  const back = new Vector3(0, 0, -PLATE_T / 2).applyEuler(euler);
  const normalEnd = new Vector3(0, 0, 30).applyEuler(euler).add(position);
  const mm = (v: Vector3) => v.clone().multiplyScalar(MM);
  return (
    <group>
      <mesh position={mm(position.clone().add(back))} rotation={euler}>
        <boxGeometry args={[PLATE * MM, PLATE * MM, PLATE_T * MM]} />
        <meshStandardMaterial color={hover ? HOVER_LIGHTEN : color} />
      </mesh>
      <Line points={[mm(position), mm(normalEnd)]} color={INK} lineWidth={1.5} />
      <Line points={[mm(position), mm(labelAt)]} color={LEADER} lineWidth={0.8} dashed dashSize={0.006} gapSize={0.004} />
      <Html position={mm(labelAt)} center zIndexRange={[hover ? 800 : 100 + index, 0]} style={{ pointerEvents: "auto" }}>
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{ ...chipStyle, display: "grid", gap: 1, boxShadow: hover ? "0 2px 6px rgba(0,0,0,0.25)" : undefined }}
        >
          <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
            <span style={{ fontWeight: 600 }}>{port.name}</span>
            {onPortChange
              ? port.xyz.map((v, c) => (
                  <NumberChip
                    key={`xyz${c}`}
                    label={"xyz"[c]}
                    value={v}
                    title={`${"xyz"[c]} of ${port.name} (mm), click to change`}
                    ariaLabel={`${port.name} ${"xyz"[c]}`}
                    onCommit={(value) => onPortChange(index, "xyz", c, value)}
                  />
                ))
              : port.xyz.map((v, c) => (
                  <span key={`xyz${c}`} style={{ color: "#5d6470" }}>
                    {"xyz"[c]}
                    {v}
                  </span>
                ))}
          </div>
          {onPortChange && (
            <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
              <span style={{ color: "#5d6470" }}>deg</span>
              {port.rpy.map((v, c) => (
                <NumberChip
                  key={`rpy${c}`}
                  label={["r", "p", "y"][c]}
                  value={v}
                  title={`${["roll", "pitch", "yaw"][c]} of ${port.name} (degrees), click to change`}
                  ariaLabel={`${port.name} ${["roll", "pitch", "yaw"][c]}`}
                  onCommit={(value) => onPortChange(index, "rpy", c, value)}
                />
              ))}
            </div>
          )}
        </div>
      </Html>
    </group>
  );
}

function Core({ ports, coreShape, color }: { ports: PrintBodyPort[]; coreShape: BodyPreviewProps["coreShape"]; color: string }) {
  const { centre, size, radius } = useMemo(() => {
    const pts = ports.map((p) => new Vector3(...p.xyz));
    const lo = pts.reduce((a, b) => a.min(b.clone()), pts[0]?.clone() ?? new Vector3());
    const hi = pts.reduce((a, b) => a.max(b.clone()), pts[0]?.clone() ?? new Vector3());
    const c = lo.clone().add(hi).multiplyScalar(0.5);
    const s = hi.clone().sub(lo).subScalar(50).max(new Vector3(40, 40, 40));
    const r = Math.max(...pts.map((p) => Math.hypot(p.x, p.y)), 40) - 12;
    return { centre: c, size: s, radius: r };
  }, [ports]);
  if (coreShape === "hub") {
    return (
      <group>
        <mesh position={[0, 0, centre.z * MM]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[40 * MM, 40 * MM, 30 * MM, 6]} />
          <meshStandardMaterial color={color} transparent opacity={0.55} />
        </mesh>
        {ports.map((p, k) => (
          <Line key={k} points={[new Vector3(0, 0, centre.z * MM), new Vector3(...p.xyz).multiplyScalar(MM)]} color={color} lineWidth={4} />
        ))}
      </group>
    );
  }
  if (coreShape === "hexagon" || coreShape === "circle") {
    return (
      <mesh position={[0, 0, centre.z * MM]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[radius * MM, radius * MM, 30 * MM, coreShape === "hexagon" ? 6 : 48]} />
        <meshStandardMaterial color={color} transparent opacity={0.45} />
      </mesh>
    );
  }
  return (
    <mesh position={centre.clone().multiplyScalar(MM)}>
      <boxGeometry args={[size.x * MM, size.y * MM, size.z * MM]} />
      <meshStandardMaterial color={color} transparent opacity={0.45} />
    </mesh>
  );
}

/** Label anchors: pushed outward from the part's centre, alternately raised, so neighbours do not stack. */
function labelAnchors(ports: PrintBodyPort[]): Vector3[] {
  const pts = ports.map((p) => new Vector3(...p.xyz));
  const centre = pts.reduce((a, b) => a.add(b), new Vector3()).multiplyScalar(pts.length ? 1 / pts.length : 0);
  return pts.map((p, k) => {
    const out = p.clone().sub(centre);
    out.z = 0;
    const dir = out.length() > 1e-6 ? out.normalize() : new Vector3(Math.cos((2 * Math.PI * k) / Math.max(1, pts.length)), Math.sin((2 * Math.PI * k) / Math.max(1, pts.length)), 0);
    return p.clone().add(dir.multiplyScalar(70)).add(new Vector3(0, 0, 45 + (k % 2) * 40));
  });
}

export function BodyPreview({ ports, dimensions, coreShape, color, onPortChange }: BodyPreviewProps) {
  const partColor = color ?? PART_GREY;
  const anchors = useMemo(() => labelAnchors(ports), [ports]);
  // Fit the camera to everything drawn: plates, dimension lines and the
  // labels hanging outside the part (so no chip is cut off at the edge).
  const reach = useMemo(
    () =>
      Math.max(
        120,
        ...ports.map((p) => Math.hypot(...p.xyz)),
        ...anchors.map((a) => a.length() + 40),
        ...dimensions.flatMap((d) => {
          const off = d.offset ?? [0, 0, 45];
          return [Math.hypot(...d.from.map((v, k) => v + off[k])), Math.hypot(...d.to.map((v, k) => v + off[k]))];
        })
      ) * MM,
    [ports, anchors, dimensions]
  );
  return (
    <div style={{ height: 340, border: "1px solid #d9d4c9", borderRadius: 6, background: "#f7f5f0" }}>
      <Canvas camera={{ position: [reach * 1.5, -reach * 1.7, reach * 1.15], up: [0, 0, 1], fov: 40 }}>
        <ambientLight intensity={0.8} />
        <directionalLight position={[2, -2, 4]} intensity={1.2} />
        <gridHelper args={[reach * 4, 20, "#d9d4c9", "#ebe7df"]} rotation={[Math.PI / 2, 0, 0]} />
        <Core ports={ports} coreShape={coreShape} color={partColor} />
        {ports.map((port, k) => (
          <PortPlate key={`${port.name}-${k}`} port={port} index={k} labelAt={anchors[k]} color={partColor} onPortChange={onPortChange} />
        ))}
        {dimensions.map((dim) => (
          <DimensionMark key={dim.key} dim={dim} />
        ))}
        <OrbitControls makeDefault enablePan={false} enableDamping={false} />
      </Canvas>
    </div>
  );
}
